<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { Menu, X } from 'lucide-vue-next';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AppearanceTabs from '@/components/AppearanceTabs.vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { AUTH_ROUTES, DASHBOARD_ROUTES, MARKETING_ROUTES } from '@/config/constants/routes';

const { t } = useI18n();

const isMobileMenuOpen = ref(false);

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

interface NavLink {
    href: string;
    label: string;
    isRoute?: boolean;
}

const navLinks: NavLink[] = [
    { href: '/#features', label: 'Features' },
    { href: '/#touri', label: 'Touri AI' },
    { href: route(MARKETING_ROUTES.pricing), label: t('pricing.page_title'), isRoute: true },
    { href: '/#contact', label: 'Contact' },
];
</script>

<template>
    <!-- Navigation -->
    <header class="fixed top-0 right-0 left-0 z-50">
        <div class="dark:bg-background/80 absolute inset-0 bg-white/80 backdrop-blur-xl"></div>
        <nav class="relative container mx-auto flex items-center justify-between px-6 py-5 lg:px-8">
            <!-- Logo -->
            <Link href="/" class="group flex items-center">
                <!-- The largest contentful paint on every marketing page: fetch it before the module graph. -->
                <AppLogoIcon class="h-10 lg:h-12" fetchpriority="high" />
            </Link>

            <!-- Desktop Navigation -->
            <div class="hidden items-center gap-1 lg:flex">
                <template v-for="link in navLinks" :key="link.href">
                    <Link
                        v-if="link.isRoute"
                        :href="link.href"
                        class="relative px-5 py-2 text-sm font-medium tracking-wide text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        {{ link.label }}
                    </Link>
                    <a
                        v-else
                        :href="link.href"
                        class="relative px-5 py-2 text-sm font-medium tracking-wide text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        {{ link.label }}
                    </a>
                </template>
            </div>

            <!-- Auth Links - Desktop -->
            <div class="hidden items-center gap-4 lg:flex">
                <AppearanceTabs :icons-only="true" />
                <Link
                    v-if="$page.props.auth.user"
                    :href="route(DASHBOARD_ROUTES.index)"
                    class="inline-flex h-10 items-center justify-center rounded-none border-2 border-orange-500 bg-orange-500 px-6 text-sm font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                >
                    Dashboard
                </Link>
                <template v-else>
                    <Link
                        :href="route(AUTH_ROUTES.login)"
                        class="px-4 py-2 text-sm font-medium tracking-wide text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        Sign in
                    </Link>
                    <Link
                        :href="route(AUTH_ROUTES.register)"
                        class="inline-flex h-10 items-center justify-center rounded-none border-2 border-orange-500 bg-orange-500 px-6 text-sm font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                    >
                        Get Started
                    </Link>
                </template>
            </div>

            <!-- Mobile Menu Button -->
            <button
                class="flex h-10 w-10 items-center justify-center text-gray-600 transition-colors hover:text-gray-900 lg:hidden dark:text-gray-400 dark:hover:text-white"
                @click="toggleMobileMenu"
            >
                <Menu v-if="!isMobileMenuOpen" class="h-5 w-5" />
                <X v-else class="h-5 w-5" />
            </button>
        </nav>

        <!-- Mobile Menu -->
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="-translate-y-4 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="-translate-y-4 opacity-0"
        >
            <div
                v-if="isMobileMenuOpen"
                class="dark:bg-background/95 absolute top-full right-0 left-0 border-b border-gray-200 bg-white/95 backdrop-blur-xl lg:hidden dark:border-gray-700"
            >
                <div class="container mx-auto space-y-1 px-6 py-4">
                    <template v-for="link in navLinks" :key="link.href">
                        <Link
                            v-if="link.isRoute"
                            :href="link.href"
                            class="block px-4 py-3 text-base font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            @click="isMobileMenuOpen = false"
                        >
                            {{ link.label }}
                        </Link>
                        <a
                            v-else
                            :href="link.href"
                            class="block px-4 py-3 text-base font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            @click="isMobileMenuOpen = false"
                        >
                            {{ link.label }}
                        </a>
                    </template>
                    <div class="flex items-center justify-between border-t border-gray-200 px-4 pt-2 pb-1 dark:border-gray-700">
                        <span class="text-sm font-medium text-gray-500">Theme</span>
                        <AppearanceTabs :icons-only="true" />
                    </div>
                    <div class="border-t border-gray-200 pt-4 dark:border-gray-700">
                        <Link
                            v-if="$page.props.auth.user"
                            :href="route(DASHBOARD_ROUTES.index)"
                            class="block w-full rounded-none border-2 border-orange-500 bg-orange-500 px-4 py-3 text-center text-base font-medium text-white dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900"
                        >
                            Dashboard
                        </Link>
                        <template v-else>
                            <Link
                                :href="route(AUTH_ROUTES.login)"
                                class="block px-4 py-3 text-base font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            >
                                Sign in
                            </Link>
                            <Link
                                :href="route(AUTH_ROUTES.register)"
                                class="mt-2 block w-full rounded-none border-2 border-orange-500 bg-orange-500 px-4 py-3 text-center text-base font-medium text-white dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900"
                            >
                                Get Started
                            </Link>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
    </header>

    <!-- Mobile Menu Overlay -->
    <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div v-if="isMobileMenuOpen" class="fixed inset-0 z-40 bg-black/70 lg:hidden" @click="isMobileMenuOpen = false" />
    </Transition>
</template>
